/**
 * Smooth Scroll Component
 *
 * Implements Lenis smooth scrolling across the entire site.
 * Integrates with GSAP ScrollTrigger for seamless parallax effects.
 *
 * @see https://github.com/studio-freight/lenis
 */

import Lenis from "lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

// Register GSAP plugin
gsap.registerPlugin(ScrollTrigger);

let lenisInstance = null;

/**
 * Initialize Lenis smooth scroll
 * Sets up integration with GSAP ScrollTrigger
 *
 * @param {Object} options - Lenis configuration options
 * @returns {Lenis} The Lenis instance
 */
export function initSmoothScroll(options = {}) {
  // Default Lenis options
  const defaultOptions = {
    duration: 1.2, // Animation duration (in seconds)
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Easing function
    orientation: "vertical", // Scroll axis
    gestureOrientation: "vertical", // Gesture axis
    smoothWheel: true, // Enable smooth scrolling for mouse wheel
    smoothTouch: false, // Disable smooth scrolling for touch devices (better performance)
    wheelMultiplier: 1, // Wheel event multiplier
    touchMultiplier: 2, // Touch event multiplier
    infinite: false, // Disable infinite scroll
    ...options,
  };

  // Create Lenis instance
  lenisInstance = new Lenis(defaultOptions);

  // Integrate with GSAP ScrollTrigger
  lenisInstance.on("scroll", ScrollTrigger.update);

  // Use GSAP ticker for smooth RAF loop
  gsap.ticker.add((time) => {
    lenisInstance.raf(time * 1000);
  });

  // Disable lag smoothing for better synchronization
  gsap.ticker.lagSmoothing(0);

  // Expose instance globally for external access
  window.lenis = lenisInstance;

  return lenisInstance;
}

/**
 * Stop/pause scrolling
 * Useful for modals, overlays, or when user interaction should be prevented
 */
export function stopScroll() {
  if (lenisInstance) {
    lenisInstance.stop();
  }
}

/**
 * Start/resume scrolling
 * Re-enables scrolling after it was stopped
 */
export function startScroll() {
  if (lenisInstance) {
    lenisInstance.start();
  }
}

/**
 * Scroll to a specific target
 *
 * @param {string|number|HTMLElement} target - Target to scroll to
 * @param {Object} options - Scroll options
 * @param {number} options.offset - Offset in pixels
 * @param {number} options.duration - Animation duration
 * @param {Function} options.easing - Easing function
 * @param {boolean} options.immediate - Skip animation
 * @param {Function} options.onComplete - Callback when scroll completes
 *
 * @example
 * // Scroll to element
 * scrollTo('#section-id');
 *
 * // Scroll to position with offset
 * scrollTo(1000, { offset: -100 });
 *
 * // Scroll with callback
 * scrollTo('.target', { onComplete: () => console.log('Done!') });
 */
export function scrollTo(target, options = {}) {
  if (lenisInstance) {
    lenisInstance.scrollTo(target, options);
  }
}

/**
 * Get the current Lenis instance
 * @returns {Lenis|null} The Lenis instance or null if not initialized
 */
export function getLenisInstance() {
  return lenisInstance;
}

/**
 * Intercept clicks on [data-scroll-to] anchors and delegate them to Lenis.
 * Prevents the browser's default instant jump and uses smooth scroll instead.
 *
 * @param {Object} options - Passed directly to Lenis scrollTo()
 * @param {number} [options.offset=0] - Pixel offset from the target
 * @param {number} [options.duration] - Override the default scroll duration
 */
export function initAnchorLinks(options = {}) {
  document.addEventListener("click", (e) => {
    const trigger = e.target.closest("[data-scroll-to]");
    if (!trigger || !lenisInstance) return;

    const target = trigger.dataset.scrollTo;
    if (!target) return;

    e.preventDefault();
    lenisInstance.scrollTo(target, options);
  });
}

/**
 * Destroy the Lenis instance
 * Cleans up event listeners and GSAP ticker
 */
export function destroySmoothScroll() {
  if (lenisInstance) {
    lenisInstance.destroy();
    gsap.ticker.remove(lenisInstance.raf);
    lenisInstance = null;
    window.lenis = null;
  }
}

// Export as object for cleaner imports
export const smoothScroll = {
  init: initSmoothScroll,
  initAnchorLinks: initAnchorLinks,
  stop: stopScroll,
  start: startScroll,
  scrollTo: scrollTo,
  getInstance: getLenisInstance,
  destroy: destroySmoothScroll,
};
